/**
 * Customer-supplied CSS sanitizer. Ported (and tightened) from the
 * legacy `App.js#sanitizeCustomCss` helper. Customers with the
 * "Custom CSS" feature flag can paste arbitrary CSS into the admin UI;
 * that string is shipped to the browser and concatenated into the
 * widget's stylesheet. Without sanitization that becomes a clear
 * `javascript:` / data-URL injection vector. The Shadow DOM stops the
 * rules from leaking out, but a malicious `expression()` or `url()`
 * still runs inside the widget's own context, so we still scrub.
 *
 * The allowlist matches the v1 implementation; we only skip rules
 * mentioning forbidden tokens, never rewrite them, so legit CSS that
 * incidentally contains the word "expression" (e.g. "expression of
 * interest" inside `content:`) continues to render. Customers caught
 * by that edge case can quote the value or split the rule.
 */
const FORBIDDEN_PATTERNS = [
  /javascript\s*:/i,
  /expression\s*\(/i,
  /vbscript\s*:/i,
  /data\s*:\s*text\/html/i,
  /@import/i,
  /behavior\s*:/i,
  /-moz-binding\s*:/i,
  /url\s*\(\s*["']?\s*javascript:/i,
  /url\s*\(\s*["']?\s*data:\s*text\/html/i,
  /<script/i,
  /<\/script>/i,
  /onload\s*=/i,
  /onerror\s*=/i,
]

export function sanitizeCustomCss(css) {
  if (typeof css !== 'string' || css.length === 0) return ''

  // Drop any rule whose serialized form contains a forbidden token.
  // We split on rule boundaries ("}" for normal rules and ";" at the
  // top level for at-rules like `@import url(...);`). We don't bother
  // with a real CSS parser because the customer-facing surface here is
  // just "scrub obvious attack tokens"; legit CSS that incidentally
  // mentions one of the forbidden patterns inside a string is rare,
  // and we'd rather drop the rule than ship an exploit vector.
  const blocks = []
  let depth = 0
  let buffer = ''
  for (let i = 0; i < css.length; i += 1) {
    const ch = css[i]
    buffer += ch
    if (ch === '{') depth += 1
    else if (ch === '}') {
      depth = Math.max(depth - 1, 0)
      if (depth === 0) {
        blocks.push(buffer)
        buffer = ''
      }
    } else if (ch === ';' && depth === 0) {
      blocks.push(buffer)
      buffer = ''
    }
  }
  if (buffer.trim().length > 0) blocks.push(buffer)

  const safe = []
  for (const raw of blocks) {
    const candidate = raw.trim()
    if (!candidate) continue
    const dangerous = FORBIDDEN_PATTERNS.some((p) => p.test(candidate))
    if (!dangerous) safe.push(candidate)
  }
  return safe.join('\n')
}
